import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { type MeUser, authApi } from "./api/auth.js";

// The auth interface (stable since M0). M1 fulfils it: hydrate from
// GET /api/auth/me, real sign-out. Feature code only depends on useAuth().
export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthUser {
  id: string;
  name: string;
  handle: string | null;
}

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

function toUser(u: MeUser): AuthUser {
  return { id: u.id, name: u.name, handle: u.handle };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  const refresh = useCallback(async () => {
    const res = await authApi.me();
    if (res.success && res.data?.user) {
      setUser(toUser(res.data.user));
      setStatus("authenticated");
    } else {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const signOut = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setStatus("anonymous");
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, refresh, signOut }),
    [status, user, refresh, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
